// Enrolling an authenticator, which is what turns a nova_admin sign-in from a
// dead end into a session that may read.
//
// NOTHING HAPPENS UNTIL SOMEBODY PRESSES BEGIN, and that is not politeness. Every
// call to `begin` mints a new secret and supersedes any enrolment already
// pending — api/src/auth/secondFactor.ts does that deliberately, so that "a
// secret displayed on a screen somebody walked away from" stops being the
// account's secret. Beginning on mount would make opening this page in a second
// tab, or pressing reload, silently invalidate the symbol being scanned in the
// first one. The person would be looking at a valid-looking QR code that matches
// nothing, and would find out at the confirmation step with no way to tell why.
//
// THE SECRET IS SHOWN AS TEXT WITHOUT A REVEAL STEP. Hiding it behind a toggle
// was considered and rejected: the QR symbol beside it encodes the same bytes and
// is readable by any camera in the room, so a toggle buys nothing against the
// threat it looks like it addresses, and it costs a step for exactly the people
// who need the text — somebody on a screen reader, or at a desktop with no phone
// camera. What it would buy is the appearance of care, which is the worst thing
// to spend an accessibility cost on.
//
// THE SECRET LIVES IN REACT STATE AND NOWHERE ELSE. Not in the session store, not
// in the url, not in a log. It is gone on reload, which is correct — there is no
// route that reads it back, so a copy that survived the page would be a copy
// nothing could ever check against the server. session/store.ts holds the wider
// argument about what may be persisted; this is the one value in the console that
// may not be persisted at all.
//
// THE SUCCESS SCREEN IS NOT A CONGRATULATION, and this is the part most likely to
// be got wrong. Confirming does not upgrade the session in this browser. The
// server says so in its own sentence — the session "was opened before it existed
// — it is unchanged and will still be refused anywhere a proved factor is
// wanted" — because reports gate on `auth_session.second_factor_id`, a column
// stamped when a session is issued and never afterwards. So a person who enrols
// and goes straight back to a report is refused again, by design, and a screen
// that said "you're all set" would have sent them there. It offers signing in
// again instead, which is the only thing that helps.
//
// AND THE CODE JUST TYPED CANNOT BE THE ONE USED TO SIGN BACK IN. Confirming
// claims that code's time step, which is the same replay guard that stops six
// digits being spent twice. The next sign-in needs the next code. That is a
// thirty-second wait that produces a refusal identical to a wrong code, so it is
// said on the screen rather than left to be discovered.

import { useEffect, useState, type ReactNode } from "react";
import { NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { Info, SecLabel } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import { beginEnrolment, confirmEnrolment, type Confirmation, type Enrolment, type Refusal } from "../api/enrol.js";
import type { ApiOptions } from "../api/client.js";
import { encodeQr } from "../qr/encode.js";
import { Qr } from "../qr/Qr.js";
import type { Session, SessionToken } from "../session/store.js";
import { FACTOR_FIELD } from "./Authorise.js";

export type EnrolProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly session: Session;
  /** See Operator.tsx: one owner for the session, and 401 is the only thing that clears it. */
  readonly onSessionEnded: (detail: string) => void;
  /**
   * Sign out and return to the login screen.
   *
   * The action offered after a successful confirmation, because a confirmed
   * factor does not change the session in hand and signing in again is the only
   * thing that will. It is the frame's own sign-out passed down rather than a
   * second implementation, so the server-side revoke happens here too.
   */
  readonly onSignOut: () => void;
  /**
   * "settings" is Settings › Sign-in security. "gate" is the same steps as the
   * whole of the first-sign-in screen (FirstFactor.tsx): a session that proved
   * no factor is shown nothing else, so the page is headed for setting one up
   * and the settings sections around the steps are left out.
   */
  readonly mode?: "settings" | "gate";
};

type Phase =
  /** Before anything has been asked for. No secret exists yet. */
  | { readonly phase: "idle" }
  | { readonly phase: "beginning" }
  | { readonly phase: "begun"; readonly enrolment: Enrolment }
  | { readonly phase: "confirming"; readonly enrolment: Enrolment }
  | { readonly phase: "confirmed"; readonly confirmation: Confirmation }
  /**
   * Begin was refused in a way no button on this screen resolves.
   *
   * Its own phase rather than a message over the idle screen, because leaving a
   * Begin button under "this service has no enrolment routes" invites somebody
   * to press it twenty times.
   */
  | { readonly phase: "blocked"; readonly refusal: Refusal };

export function Enrol({ api, token, session, onSessionEnded, onSignOut, mode = "settings" }: EnrolProps): ReactNode {
  const [state, setState] = useState<Phase>({ phase: "idle" });
  // Whether a secret has been shown on this page. Once it has, the page is the
  // steps until setup ends, including while "Start over" fetches a new one.
  const [begunOnce, setBegunOnce] = useState(false);
  useEffect(() => {
    if (state.phase === "begun") setBegunOnce(true);
  }, [state.phase]);
  const [code, setCode] = useState("");
  /**
   * A refusal of the code, shown without leaving the `begun` phase.
   *
   * Separate from `state` because a wrong code changes nothing about the
   * enrolment: the same secret is still pending, the same QR symbol is still
   * valid, and folding this into the phase would risk a redraw that replaced the
   * symbol somebody is halfway through scanning.
   */
  const [codeProblem, setCodeProblem] = useState<string | null>(null);

  async function begin(): Promise<void> {
    setState({ phase: "beginning" });
    setCode("");
    setCodeProblem(null);

    const result = await beginEnrolment(api, token);

    if (result.ok) {
      setState({ phase: "begun", enrolment: result.value });
      return;
    }

    if (result.kind === "session_ended") {
      onSessionEnded(result.detail);
      return;
    }

    setState({ phase: "blocked", refusal: result });
  }

  async function confirm(enrolment: Enrolment): Promise<void> {
    setState({ phase: "confirming", enrolment });
    setCodeProblem(null);

    const result = await confirmEnrolment(api, token, code.trim());

    if (result.ok) {
      setState({ phase: "confirmed", confirmation: result.value });
      return;
    }

    if (result.kind === "session_ended") {
      onSessionEnded(result.detail);
      return;
    }

    // A refused code returns to the enrolment rather than discarding it: the
    // pending secret is untouched by a wrong guess, so throwing the screen away
    // would mean a mistyped digit costs a fresh secret and another scan.
    if (result.kind === "code_refused") {
      setState({ phase: "begun", enrolment });
      setCodeProblem(result.detail);
      setCode("");
      return;
    }

    setState({ phase: "blocked", refusal: result });
  }

  // Offered to a session that proved no factor, whatever its role: since #202
  // every role needs one, so a Viewer who has signed in at all has one, and the
  // service refuses a second enrolment. Hidden from a session that used one and
  // from a tab that does not know, because that is the same button on a guess.
  // On the gate it is the whole point of the screen. The rule is named so a
  // mutation that ignores the flag has one line to break.
  const offerEnrol = mode === "gate" || session.secondFactorProved === false;

  // Once setup has begun the page is the steps, as the design draws it: the
  // explanations above them have been read, and a person scanning a secret is
  // better served by nothing else on the page.
  //
  // "Start over" goes back through `beginning` while the new secret is fetched,
  // so a page that has already shown the steps keeps to them meanwhile rather
  // than putting the explanations back for a moment. Found by Bugbot on #159.
  const restarting = state.phase === "beginning" && begunOnce;
  const settingUp =
    state.phase === "begun" || state.phase === "confirming" || state.phase === "confirmed" || restarting;

  return (
    <div className="stack">
      {/* IT WAS HEADED "SETTINGS" AND SAID "MORE WILL LAND HERE", which was
          true and is now the wrong heading: Settings is three pages and this is
          the second of them. The lede that replaces it says what the pack's
          says, because the pack is right about the thing worth saying first —
          there is no password on this service, and the two factors do different
          jobs. The emailed code is how the service knows which sign-in you are;
          the authenticator is what a report is refused without. Somebody who
          has just been refused a report is on this page to learn the second
          half. */}
      {mode === "gate" ? (
        <PageHead
          title="Set up your authenticator"
          lede="Everyone who uses the Console signs in with the emailed code and an authenticator app. Set yours up before you start."
        />
      ) : (
        <PageHead
          title="Sign-in security"
          lede="How you sign in, and the authenticator reports need."
          explain="info"
        />
      )}

      {settingUp || mode === "gate" ? null : (
        <>
          <section>
            <SecLabel>How sign-in works</SecLabel>
            <div className="panel panel-stack">
              <p>
                There is no password. Enter your email, then use the code we send.
              </p>
              <p className="help help-tight">
                Everyone signs in with the email code and an authenticator app.
              </p>
            </div>
          </section>

          <section>
            <SecLabel>Email passcode</SecLabel>
            <div className="panel panel-stack">
              <div className="fac">
                <div>
                  <p className="fac-t">
                    {session.email === null ? "Sent to the address you signed in with" : `Sent to ${session.email}`}
                  </p>
                  <p className="fac-d">
                    Each code expires quickly and works once. Email codes cannot be turned off.
                  </p>
                </div>
                <span className="tag tag-good">Always on</span>
              </div>
              {/* THE PACK HAS "LAST SENT" AND "DELIVERABILITY" HERE AND BOTH ARE
                  ABSENT. Nothing records when a code was last sent to an address in
                  a form a browser could read, and no bounce is kept against an
                  identity at all — delivery.ts posts to Mailgun and does not write
                  the outcome anywhere this console can reach. A row reading "no
                  bounce recorded against this address" would be a reassurance
                  nobody checked. Its two buttons are absent for the same reason:
                  there is no route that changes an address and none that sends a
                  test code to a signed-in session. */}
              <p className="help help-tight">
                Ask Nova to change the account email.
              </p>
            </div>
          </section>

          {/* Every role's since #202, because every role needs one. */}
          <AuthenticatorCard session={session} />
        </>
      )}

      {restarting ? <Loading rows={3} cols={1} label="Starting again with a new secret…" /> : null}

      {offerEnrol && (state.phase === "idle" || (state.phase === "beginning" && !begunOnce)) ? (
        <section>
          <h2 className="sec">Before you start</h2>
          <div className="panel panel-stack">
            <p>
              Open the authenticator app on the phone you plan to keep using.
            </p>
            <p className="help help-tight">
              {/* Said before the secret appears rather than after, because it is the
                  fact that changes what somebody does: it is the reason not to
                  begin twice, and after the second press it is too late to be
                  useful. */}
              Starting again replaces any unfinished setup, including one open in another tab.
            </p>
            <NoticeBox kind="amber">
              <p>
              <strong>There are no recovery codes.</strong> If you lose the device, ask Nova to reset
              your authenticator, then set it up again.
              </p>
            </NoticeBox>
            <div className="acts acts-flush">
              <button
                type="button"
                className="btn"
                disabled={state.phase === "beginning"}
                onClick={() => void begin()}
              >
                {state.phase === "beginning" ? "Starting…" : "Begin"}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {state.phase === "begun" || state.phase === "confirming" ? (
        <Pending
          enrolment={state.enrolment}
          code={code}
          onCode={setCode}
          problem={codeProblem}
          busy={state.phase === "confirming"}
          onConfirm={() => void confirm(state.enrolment)}
          onStartOver={() => void begin()}
        />
      ) : null}

      {state.phase === "confirmed" ? (
        <Confirmed confirmation={state.confirmation} onSignOut={onSignOut} />
      ) : null}

      {state.phase === "blocked" ? (
        <section>
          <NoticeBox kind="bad" title={titleFor(state.refusal)} alert>
            {/* The server's sentence. On `already_enrolled` it names both ways out,
                and on `not_offered` it is the difference between "ask Nova to
                configure this" and "try again", which no wording of ours could
                improve on. */}
            <p>{state.refusal.detail}</p>
          </NoticeBox>
          {/* NO RETRY ON THE TWO REFUSALS A RETRY CANNOT RESOLVE. An account that
              already has a factor needs a sign-in that proves it, and a service
              with no sealing key needs configuring; a button under either is a
              button somebody presses twenty times and then reports as broken. The
              other refusals — an expired enrolment, a 5xx, a dropped network —
              are all resolved by asking again. */}
          {state.refusal.kind === "already_enrolled" || state.refusal.kind === "not_offered" ? null : (
            <div className="acts">
              <button type="button" className="btn btn-quiet" onClick={() => void begin()}>
                Try again
              </button>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

/**
 * What this session proved, which is not what the account holds.
 *
 * routes.ts sends `second_factor_proved` as `auth_session.second_factor_id` as a
 * boolean — what THIS SESSION proved. False is not "the account has none", and a
 * missing stored field is not false: an older tab never recorded the answer, and
 * reading that as proved-none would tell somebody who used an authenticator this
 * morning that they have none.
 *
 * THE ENROL ROUTE REFUSES AN ACCOUNT THAT ALREADY HAS ONE. Offering Begin after
 * a sign-in that used the authenticator is a button that cannot work. Offering
 * it when this tab does not know is the same button on a guess. Only a session
 * that proved no factor is offered it, because that is the one case where adding
 * one and signing in again is what the reports will then accept.
 */
function AuthenticatorCard({ session }: { session: Session }): ReactNode {
  const proved = session.secondFactorProved;

  // The design's row: what this sign-in proved, in a sentence, and a tag that
  // says it in a word. Nothing here names a device or a factor, because the
  // session records only whether one was used.
  return (
    <section>
      <SecLabel>Authenticator app</SecLabel>
      <div className="panel panel-stack">
        <div className="fac">
          <div>
            <p className="fac-t">
              {proved === true
                ? "An authenticator was used on this sign-in"
                : proved === false
                  ? "No authenticator on this sign-in"
                  : "This sign-in did not record it"}
            </p>
            <p className="fac-d">
              {proved === true
                ? "This sign-in used both the email code and an authenticator. If you lose the device, ask Nova to reset your authenticator."
                : proved === false
                  ? "Set up an authenticator, then sign in again to open reports."
                  : "This older session does not say whether an authenticator was used. Sign out and back in to refresh it."}
            </p>
          </div>
          <span className={proved === true ? "tag tag-good" : proved === false ? "tag tag-warn" : "tag tag-off"}>
            {proved === true ? "In force" : proved === false ? "Not set up" : "Unknown"}
          </span>
        </div>
        {proved === true ? (
          <NoticeBox kind="amber" title="There are no recovery codes">
            <p>If you lose the device, ask Nova to reset your authenticator, then set it up again.</p>
          </NoticeBox>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The secret, in both forms, and the field that proves it.
 *
 * The two representations are the same bytes and the server encodes both — the
 * console does not derive one from the other. Deriving the base32 from the URI's
 * query string, or the URI from the secret, would be a second implementation of
 * an encoding whose failure mode is an app producing plausible codes that match
 * nothing.
 */
function Pending({
  enrolment,
  code,
  onCode,
  problem,
  busy,
  onConfirm,
  onStartOver,
}: {
  enrolment: Enrolment;
  code: string;
  onCode: (value: string) => void;
  problem: string | null;
  busy: boolean;
  onConfirm: () => void;
  onStartOver: () => void;
}): ReactNode {
  const symbol = encodeQr(enrolment.otpauthUri);

  return (
    <>
      <section>
        <h2 className="sec">Step 1: add Nova to your app</h2>

        {/* The design's split: the symbol in its tile beside the secret. */}
        <div className="panel split">
          <div className="qr">
            {symbol.ok ? (
              <Qr
                matrix={symbol.matrix}
                // No part of the secret in the label. It is read aloud, copied by
                // assistive technology and surfaced by tooling that never touches
                // the page, and the text field below is where somebody who cannot
                // scan is meant to get it.
                label="A QR code holding this enrolment. It carries the same secret as the text beside it."
              />
            ) : (
              <NoticeBox kind="amber" title="Could not create the QR code">
                <p>{symbol.problem}</p>
                <p>Enter the secret manually instead.</p>
              </NoticeBox>
            )}
          </div>

          <div className="panel-stack">
            <p>Scan this with the app, or add it by hand using the secret below.</p>

            <div className="field">
              <label className="label" htmlFor="console-secret">
                Secret
                {/* Behind the (i), as the design keeps it: the service's own
                    algorithm, digits and period, which the app must match. */}
                <Info>
                  Enter it as one block. Use {enrolment.algorithm}, {enrolment.digits} digits, every{" "}
                  {enrolment.periodSeconds} seconds.
                </Info>
              </label>
              <input
                id="console-secret"
                // .secret-field is the mono face and the tracking, which is what
                // makes a hand-copied base32 block legible: it is the difference
                // between telling I from l and O from 0 and guessing. The design
                // puts it on this field and on no other.
                className="input secret-field"
                // Read-only rather than disabled: a disabled input is skipped by
                // keyboard navigation and its text cannot be selected, and
                // selecting this text is the entire purpose of the field.
                readOnly
                value={enrolment.secret}
                // Off, because a password manager is welcome to this and a
                // browser's form-history is not: this value would otherwise sit
                // in autofill suggestions on a shared machine long after the
                // enrolment it belongs to expired.
                autoComplete="off"
                spellCheck={false}
                onFocus={(event) => event.currentTarget.select()}
              />
            </div>

            <p className="help help-tight">
              This secret is shown once and expires at{" "}
              {new Date(enrolment.expiresAt).toLocaleTimeString()}. Start again if you lose it.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="sec">Step 2: confirm the code</h2>
        <div className="panel panel-stack">

        {/* The server's sentence about what has not happened yet: the factor is
            not in force until this step succeeds. Drawn here rather than at the
            top, because it is about the button below it. */}
        <p>{enrolment.detail}</p>

        {problem === null ? null : (
          <NoticeBox kind="bad" title="Code not accepted" alert>
            {/* Verbatim, and deliberately not diagnosed further. The server gives
                one answer for a wrong code and a spent one — see api/enrol.ts —
                and its sentence carries the action for both. */}
            <p>{problem}</p>
            <p className="help help-tight">You do not need to scan the QR code again.</p>
          </NoticeBox>
        )}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            onConfirm();
          }}
        >
          <div className="field">
            <label className="label" htmlFor="console-enrol-code">
              Authenticator code
            </label>
            <input
              id="console-enrol-code"
              className="input"
              // `text`, not `number`, for SignIn.tsx's reason: a number input
              // strips the leading zero a six-digit code can start with.
              type="text"
              inputMode="numeric"
              {...FACTOR_FIELD}
              name="xp-factor"
              maxLength={8}
              value={code}
              onChange={(event) => onCode(event.target.value)}
              onKeyDown={(event) => {
                // Same reason as SignIn.tsx: form-action 'none' can swallow the
                // native submit that Enter would otherwise fire.
                if (event.key !== "Enter" || busy || code.trim() === "") return;
                event.preventDefault();
                onConfirm();
              }}
              required
            />
          </div>
          <div className="acts">
            <button type="submit" className="btn" disabled={busy || code.trim() === ""}>
              {busy ? "Checking…" : "Confirm"}
            </button>
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={onStartOver}>
              Start over with a new secret
            </button>
          </div>
        </form>
        </div>
      </section>
    </>
  );
}

/**
 * Confirmed, and the two things that are still true.
 *
 * Deliberately not a success page with a link back to the reports. See the header:
 * the session in this browser is unchanged, so that link would lead straight to
 * the same refusal.
 */
function Confirmed({
  confirmation,
  onSignOut,
}: {
  confirmation: Confirmation;
  onSignOut: () => void;
}): ReactNode {
  return (
    <>
      <NoticeBox kind="good" title="Authenticator is set up">
        {/* The server's own sentence, which is the one that says this session is
            unchanged and will still be refused. A paraphrase would lose it, and
            losing it is how somebody concludes enrolment did not work. */}
        <p>{confirmation.detail}</p>
      </NoticeBox>

      <NoticeBox kind="amber" title="Wait for a new code before signing in">
        <p>
          The code you just used cannot be used again. Wait for the app to show the next one.
        </p>
      </NoticeBox>

      <div>
        <div className="acts acts-flush">
          <button type="button" className="btn" onClick={onSignOut}>
            Sign out and sign in again
          </button>
        </div>
        <p className="id" style={{ marginTop: 16 }}>
          Factor {confirmation.factorId}
        </p>
      </div>
    </>
  );
}

function titleFor(refusal: Refusal): string {
  switch (refusal.kind) {
    case "already_enrolled":
      return "This account already has an authenticator";
    case "not_offered":
      return "Authenticator setup is unavailable";
    case "nothing_to_confirm":
      return "No setup is waiting";
    case "unavailable":
      return "Could not set up the authenticator";
    case "malformed":
      return "Could not complete setup";
    case "unreachable":
      return "Could not connect";
    case "code_refused":
    case "session_ended":
      // Neither reaches this screen: a refused code stays on the enrolment and a
      // dead session leaves for the login screen. Named rather than defaulted, so
      // that adding a kind to Refusal is a type error here rather than a blank
      // heading over somebody's refusal.
      return "Could not complete setup";
  }
}
